import { DEVICE_KINDS, SHIPPED_LOCALES } from '@croissants/domain/vocabulary';
import { z } from 'zod';
import { ErrorMessageKeySchema } from './messages';

/**
 * Primitives shared by every mount.
 *
 * Two rules are load-bearing here and are enforced by the schemas rather than by
 * convention:
 *
 *  - Money is an integer count of minor units (PRD NFR-003, tech-stack section 8).
 *    There is no float money type in this package and no schema may introduce one.
 *  - Instants are UTC ISO-8601 strings; a wall-clock time is always paired with the
 *    IANA timezone of the location it belongs to (tech-stack section 8).
 */

/** Integer minor units. 1250 is 12.50 EUR. Never a float, never a formatted string. */
export const MinorUnits = z
  .int()
  .describe('Amount in integer minor units of the currency (cents for EUR)');
export type MinorUnits = z.infer<typeof MinorUnits>;

/** Non-negative minor units - the common case for prices, fees and totals. */
export const MinorUnitsNonNegative = MinorUnits.min(0);

/** ISO 4217. EUR at launch; the field exists so a second currency is not a migration. */
export const CurrencyCode = z.enum(['EUR']);
export type CurrencyCode = z.infer<typeof CurrencyCode>;

/**
 * An amount with the currency it is counted in - the ONE money shape on the
 * wire, and deliberately the same field name the domain's own `Money` carries
 * (`amountMinor`).
 *
 * It said `amount` until the counter ticket needed a self-describing figure,
 * and nothing on the wire had ever used it: every money field elsewhere is a
 * bare `MinorUnitsNonNegative` beside a sibling `currency`, which works while a
 * response carries exactly one currency and stops working the moment a nested
 * row needs its own. `amountMinor` is what the domain, the services and every
 * adapter call it, so a value crossing the boundary keeps its name rather than
 * being renamed twice per round trip.
 */
export const Money = z.object({
  amountMinor: MinorUnits,
  currency: CurrencyCode,
});
export type Money = z.infer<typeof Money>;

/**
 * `Money` that cannot be below zero: a figure that is a sum of prices by
 * construction, stated so on the wire (the counter's tile, BAK-029).
 */
export const MoneyNonNegative = z.object({
  amountMinor: MinorUnitsNonNegative,
  currency: CurrencyCode,
});
export type MoneyNonNegative = z.infer<typeof MoneyNonNegative>;

/** A rate held as basis points so no percentage is ever a float. 3000 bp is 30%. */
export const BasisPoints = z
  .int()
  .min(0)
  .max(10_000)
  .describe('Rate in basis points; 10000 = 100%');
export type BasisPoints = z.infer<typeof BasisPoints>;

/** UTC instant, ISO-8601 with an explicit offset. */
export const Instant = z.iso.datetime({ offset: true });
export type Instant = z.infer<typeof Instant>;

/** Calendar date in the operational location's own timezone: YYYY-MM-DD. */
export const ServiceDay = z.iso.date();
export type ServiceDay = z.infer<typeof ServiceDay>;

/**
 * The number, letter or word on the door - and never a blank one.
 *
 * A door is two facts on this platform since the position became translatable
 * (WEB-017, NFR-006): a `DoorPosition` the reader's own language names, and
 * this, which is transcribed as it is written. `dispatch.ts` composes the
 * courier's line from BOTH, and it treats stating either one as correcting the
 * whole door - so a blank number is not a correction, it is nothing, and a
 * plain `z.string().nullable()` could not tell the two apart. `''` had to be
 * read as "no door" by every reader separately, and `courierBrief` is the one
 * that remembered: without its own guard a guest who tabbed through the box
 * would have taken away a door the apartment record HAD.
 *
 * So the boundary answers it instead, in the direction every client already
 * sends: trimmed, and `''` becomes `null`. Normalising rather than refusing,
 * because this shape is a RESPONSE as often as a request - a row written before
 * this existed reads back as the null it means, where a `.min(1)` would fail
 * response validation on it and take the whole apartment brief with it.
 */
export const DoorNumber = z
  .string()
  .max(40)
  .nullable()
  .transform((value) => {
    const trimmed = value?.trim() ?? '';
    return trimmed === '' ? null : trimmed;
  });
export type DoorNumber = z.infer<typeof DoorNumber>;

/** Wall-clock HH:mm in the location's timezone. */
export const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export type LocalTime = z.infer<typeof LocalTime>;

/** IANA zone name. Europe/Paris at launch; never assumed anywhere in code. */
export const TimeZone = z.string().min(1).max(64);
export type TimeZone = z.infer<typeof TimeZone>;

export const Uuid = z.uuid();
export type Uuid = z.infer<typeof Uuid>;

/**
 * A URL that POINTS AT a stored object, and can never BE one.
 *
 * `z.url()` accepts a `data:` URI, because a data URI is a perfectly valid URL.
 * That is how images came to live inside database rows: every image field on the
 * platform declared `z.url()`, so bytes passed validation everywhere - a guest's
 * issue photograph, a courier avatar from the delivery simulator, a catalogue
 * photograph from an out-of-band import. Between the demo and production
 * databases that was 2.6 MB of picture in rows, re-read through Hyperdrive by
 * every screen that listed them, and a 474 KB storefront the browser spent
 * twenty seconds decoding (docs/portal-latency-audit.html).
 *
 * Bytes belong in R2 and rows hold a reference. This is that rule stated where
 * it can be enforced rather than remembered, so the boundary rejects the bytes
 * on the way in - and `RESPONSE_VALIDATION` catches a handler that would answer
 * with them on the way out.
 *
 * `blob:` is refused for the same reason and one more: it is meaningless outside
 * the one browsing context that minted it, so it can only ever be a mistake.
 *
 * The name says OBJECT rather than image because the rule never was about
 * images: the counter fleet's APK is served from R2 through the worker
 * (`HandheldUpdateOffer.apkUrl`, docs/plans/handheld-fleet.html 3), and a
 * `data:` URI there would be an executable inlined on the wire - the same
 * failure, one order of magnitude worse. `ImageUrl` is an alias below, so the
 * rule is written once and every existing import keeps working.
 */
export const RemoteObjectUrl = z.url().refine((value) => !/^\s*(data|blob):/i.test(value), {
  message: 'this URL must reference a stored object, never carry the object itself',
});
export type RemoteObjectUrl = z.infer<typeof RemoteObjectUrl>;

/**
 * `RemoteObjectUrl` where the object is a picture. The same schema, under the
 * name a hundred image fields already declare - kept so those fields go on
 * saying what they hold, and so the refinement can never be restated.
 */
export const ImageUrl = RemoteObjectUrl;
export type ImageUrl = z.infer<typeof ImageUrl>;

/**
 * What a ROW holds for a picture: the object's key in the MEDIA bucket, and
 * nothing else (docs/plans/media-identity-not-address.html).
 *
 * `ImageUrl` above guards the WIRE and is unchanged - a client still receives
 * an absolute `https://` URL it can put straight in a `src`. This is the other
 * representation, and the split is the whole point: an address encodes host,
 * scheme, CDN vendor and signing scheme, all of them deployment details with a
 * shelf life, and storing one is why moving domain needed 1 616 rows rewritten.
 * A key is the fact that stays true, and it is the only representation a signed
 * URL can have, since a signed URL expires and therefore cannot be stored at all.
 *
 * `<prefix>/<...>/<name>.<ext>` - the prefix says what the object IS, which is
 * what decides whether it is served publicly or against a signature. No scheme,
 * no host, no leading slash, no `..`, no query string: every one of those is a
 * value that would resolve somewhere other than this platform's own bucket.
 */
export const MediaKey = z
  .string()
  .min(3)
  .max(200)
  .regex(
    /^[a-z0-9][a-z0-9._-]*(\/[a-z0-9][a-z0-9._-]*)+$/i,
    'a media key is <prefix>/<path>, with no scheme, host, leading slash or dot segment',
  );
export type MediaKey = z.infer<typeof MediaKey>;

/**
 * Public identifier: safe to put in a URL (PRD GST-001).
 *
 * MINTED ones are opaque and non-enumerable - an order's code, a QR token - and
 * those are what the eight-character floor was written for. A CATALOGUE
 * product's code is the other kind: it is AUTHORED, in the partner's own
 * workbook, and a curator and an invoice line both name the product by it
 * (founder, 2026-08-28). A workbook code is a word, and two of Le Grenier a
 * Pain's are shorter than eight characters - `OPERA` is five, `BEURRE` six - so
 * the floor is the length below which a string is not an identifier at all.
 *
 * Nothing was relying on the old number: it is a sanity check on a shape, never
 * a guarantee, and every minted id here is twelve hex characters or more by
 * construction rather than by this schema.
 */
export const PublicId = z.string().min(3).max(64);
export type PublicId = z.infer<typeof PublicId>;

export const Locale = z.enum(SHIPPED_LOCALES);
export type Locale = z.infer<typeof Locale>;

/**
 * What the signed-in person, rather than their organization, has chosen (NFR-006).
 *
 * One language per user, and it is the only one: it selects the interface AND the
 * language anything the platform sends them is written in. The two used to be
 * separate controls sitting side by side in the partner settings form - an
 * interface language kept in the browser and a notification language kept on the
 * organization - which meant a member could read the dashboard in English while
 * being written to in French, and two members of one organization could not
 * differ at all. Naming them side by side made the difference visible; it did not
 * make it wanted.
 *
 * This is deliberately a preferences OBJECT rather than a bare locale field. The
 * next per-user preference belongs here beside it rather than in a second
 * endpoint, and a client that PATCHes one key must not have to resend the others.
 */
export const UserPreferences = z.object({
  locale: Locale,
});
export type UserPreferences = z.infer<typeof UserPreferences>;

/** E.164. Required on every order for delivery contact (PRD GST-006). */
export const PhoneE164 = z.string().regex(/^\+[1-9]\d{6,14}$/);
export type PhoneE164 = z.infer<typeof PhoneE164>;

export const Email = z.email();
export type Email = z.infer<typeof Email>;

/**
 * The counter's PIN, on the wire: exactly four digits
 * (docs/plans/device-pairing.html 2.3).
 *
 * It was `z.string().min(4).max(12)` on the one endpoint that took a PIN, and
 * both keypads capped themselves at four anyway - so the contract accepted
 * eight values no client could ever send and no server could ever have stored.
 * Four is what the product means, stated once and used by all four PIN-bearing
 * bodies.
 *
 * The SHAPE is this schema and nothing else: `isWeakDevicePin` and
 * `isValidDevicePin` below ask it rather than restating the four digits. Whether
 * a well-formed code is ACCEPTABLE - `1234`, `0000` - is those two functions,
 * kept apart from the shape on purpose: a refused weak PIN is a message the
 * counter has to read ("choose another one"), not a 422 on a malformed request.
 */
export const DevicePin = z.string().regex(/^\d{4}$/);
export type DevicePin = z.infer<typeof DevicePin>;

/*
 * The ONE rule that decides whether a typed PIN is acceptable.
 *
 * The PIN is a curtain, not a credential: it stops a passer-by at the counter
 * from tapping a ticket, and what actually protects the API is the paired
 * device row. That is exactly why the rule is stated once rather than in a
 * keypad. Four surfaces ask the same question - the Sunmi keypad, the browser
 * tablet's keypad, `POST /b/devices/pin` and the operator's `pin/set` on both
 * admin portals - and a rule enforced only on the keypads is a rule a replayed
 * request walks straight past. It lives here, beside its wire shape, because
 * this is the one package the worker, `packages/ui` and every app can all
 * import; there is no second TypeScript copy.
 *
 * The Sunmi is outside the pnpm graph, so `DevicePin.java` in
 * apps/bakery-handheld is the one copy, and `device-pin.cases.json` beside this
 * file pins it: `device-pin.test.ts` and `DevicePinTest.java` both run every
 * case - the TypeScript one in CI, the Java one only by hand, since no workflow
 * runs the handheld's unit tests (apps/bakery-handheld/CLAUDE.md). Change the
 * rule by adding a case to that table first.
 *
 * What is refused is deliberately short: the four ascending/descending runs a
 * stranger tries first, and the ten repeated digits. It is not a password
 * policy - a bakery counter shares one code between everyone who works there
 * and writes it on a card by the till, so refusing `2580` (the keypad's middle
 * column) would cost a support call and buy nothing. Ten thousand codes minus
 * fourteen, throttled per LOCATION after five failures, is the shape of this
 * defence; the entropy was never meant to carry it alone.
 */

/** The length both keypads fill and the only length the wire accepts. */
export const DEVICE_PIN_LENGTH = 4;

/**
 * The runs. Four identical digits are refused too, but by the predicate below
 * rather than by ten more entries here: `0000`..`9999` as literals is a list
 * somebody edits and gets wrong, and the property is what is meant.
 */
export const WEAK_DEVICE_PINS: readonly string[] = ['1234', '4321', '0123', '3210'];

/**
 * Is this one of the codes a stranger tries first?
 *
 * Asked only of a well-formed PIN: a value that is not four digits is not weak,
 * it is simply not a PIN - which is what lets a screen say which of the two
 * things went wrong. `isValidDevicePin` is the one every call site should use.
 */
export function isWeakDevicePin(pin: string): boolean {
  if (!DevicePin.safeParse(pin).success) return false;
  if (WEAK_DEVICE_PINS.includes(pin)) return true;
  return [...pin].every((digit) => digit === pin[0]);
}

/** The rule, whole: the wire shape, and not one of the weak codes. */
export function isValidDevicePin(pin: string): boolean {
  return DevicePin.safeParse(pin).success && !isWeakDevicePin(pin);
}

/**
 * What is standing on the counter: the Sunmi handheld or the browser tablet.
 * Reported by the app at pairing and on every heartbeat, and shown on the
 * operator's Devices list - "which of the two is this" is the first question
 * anybody asks about a row there.
 */
export const DeviceKind = z.enum(DEVICE_KINDS);
export type DeviceKind = z.infer<typeof DeviceKind>;

export const Latitude = z.number().min(-90).max(90);
export const Longitude = z.number().min(-180).max(180);

export const GeoPoint = z.object({
  latitude: Latitude,
  longitude: Longitude,
});
export type GeoPoint = z.infer<typeof GeoPoint>;

/**
 * A promised delivery window. 30 minutes long on a 15-minute cadence, so windows
 * overlap by design (PRD RTE-028). Length and cadence are separate configured
 * values and must never be assumed equal.
 */
export const DeliveryWindow = z.object({
  start: Instant,
  end: Instant,
  /** Wall-clock rendering for the guest, in the property's timezone. */
  localStart: LocalTime,
  localEnd: LocalTime,
  serviceDay: ServiceDay,
  timeZone: TimeZone,
});
export type DeliveryWindow = z.infer<typeof DeliveryWindow>;

/** Cursor pagination. Offsets are not used: order lists move while being read. */
export const PageRequest = z.object({
  cursor: z.string().max(256).optional(),
  limit: z.int().min(1).max(200).default(50),
});
export type PageRequest = z.infer<typeof PageRequest>;

export const pageOf = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });

/**
 * One client's whole portfolio of QR codes as ONE PDF (WEB-019: "any selection
 * of properties - including all of them - in a single action"). Declared here
 * because the owner's portal and Internal both download it, from the same
 * worker function (`portfolio-sheet.ts`), and a shape written twice drifts.
 *
 * `fileName` travels with the link because the portals SAVE the file rather
 * than open it: a fetched blob carries none of the response's headers, so
 * without it every download would be named by the portal's own guess.
 */
export const QrPortfolioSheet = z.object({
  printUrl: z.url(),
  fileName: z.string().min(1).max(200),
  propertyCount: z.int().positive(),
});
export type QrPortfolioSheet = z.infer<typeof QrPortfolioSheet>;

/**
 * Client-supplied idempotency key. Required on every mutation that moves money or
 * creates an order, a dispatch or a refund (PRD PAY-006, NFR-003); enforced by a
 * unique constraint in Postgres, never by an in-memory cache.
 */
export const IdempotencyKey = z.string().min(8).max(255);
export type IdempotencyKey = z.infer<typeof IdempotencyKey>;

/** The single error shape every mount returns. Typed domain errors map onto it. */
export const ApiErrorCode = z.enum([
  'support_case_stale',
  'refund_over_charged',
  'support_case_invalid',
  'refund_unauthorized',
  'refund_order_not_final',
  'support_case_not_found',

  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'gone',
  'unprocessable',
  'rate_limited',
  'idempotency_conflict',
  'service_paused',
  'not_servable',
  'window_unavailable',
  /* RTE-034: the chosen today window slipped behind the guest's arrival
   * estimate (`now + the answer allowance + the counter's p75 + travel`)
   * between the screen rendering and the guest pressing Pay. Its own
   * code rather than `window_unavailable`, because the guest's next move is
   * different: nothing about the basket or the bakery is wrong, the clock moved. */
  'window_passed',
  /* GST-010: a morning of a stay that was servable when the guest reviewed it
   * is not any more. Its own code rather than `window_unavailable`, because the
   * guest's next move is different: the time they chose is fine, one of the
   * mornings is gone, and what they do is look at the order again and pay for
   * what is left. Nothing is taken - this is refused before the payment. */
  'stay_morning_unavailable',
  /* OPS-025 / GST-042: neither ordering mode is on, or the one this order needs
   * was switched off after the screen rendered. */
  'ordering_closed',
  /* BAK-007 / BAK-040 / RTE-032: the provider refused to move a pickup because
   * the courier is already on the way to it. Not a failure - the answer - so
   * the counter reads "the courier is already on the way" rather than a 500. */
  'courier_en_route',
  /* The order camera (founder, 2026-10-08), all 409 and all the counter's:
   * a SKIPPED Ready on a shop whose photographs are MANDATORY; a photograph of
   * a bag that is not ready (or no longer exists as one); the eleventh. */
  'bag_photo_required',
  'bag_photo_not_ready',
  'bag_photo_limit',
  'capacity_exhausted',
  'minimum_basket_not_met',
  'cancellation_closed',
  'payment_failed',
  'provider_unavailable',
  'provider_limit_violated',
  'capability_unavailable',
  /*
   * Device pairing (docs/plans/device-pairing.html 2.2). Every one of these is
   * 403 except where noted, and the reason they are named codes rather than a
   * bare `forbidden` is that BOTH counter apps wipe their pairing on ANY 401
   * (`Api.java`, `session.tsx`). So 401 is reserved for exactly one meaning -
   * this token is dead, pair again - and everything a counter can recover from
   * has to arrive as a 403 the app can tell apart.
   */
  'pin_rejected',
  /** Carries `until` on the error body: when the counter may try again. */
  'pin_locked',
  /**
   * Both RETIRED by the founder decision of 2026-08-23, and kept in this union
   * on purpose for one release.
   *
   * Nothing on the server answers either any more: pairing asks for no PIN, so
   * it cannot demand one (`pairing_requires_pin`), and a pending reset no
   * longer refuses a pairing (`pin_being_reset`). But both counter apps SHIP
   * with a branch on each - a Sunmi on a shelf is not a deploy - and deleting
   * the names here would only stop those branches compiling, not stop them
   * being installed. They come out when the apps that read them do.
   */
  'pin_being_reset',
  'pairing_requires_pin',
  /**
   * 409: two devices were told to choose after ONE admin reset and the second
   * arrived late. It is the only route to this code now - a device that has
   * just paired overwrites and never loses.
   */
  'pin_already_set',
  'org_suspended',
  'location_suspended',
  /** 404, with no body detail: the code must not enumerate bakeries. */
  'unknown_pairing_code',
  /** 400: asked to send to the stored contact, and there is none. */
  'no_contact',
  /** 400: asked to send the PIN, and none has been chosen yet. */
  'no_pin',
  /**
   * 400: an operator typed a PIN `isValidDevicePin` refuses - `1234`, `0000`,
   * one of the four sequences. Its own code rather than a field error because
   * the rule is the PIN rule both keypads already enforce, and the dialog
   * shows the same sentence a counter sees ("ce code est trop simple").
   */
  'weak_pin',
  /**
   * 429, carrying `until`: this counter has already asked for something within
   * the interval that action is allowed on. Distinct from `pin_locked`, which
   * is a refusal to try again after WRONG attempts; this one is a refusal to
   * repeat a CORRECT one, and the screen says "code deja envoye" rather than
   * "trop d'essais".
   */
  'too_many_requests',
  /**
   * 400 (RTE-023): an operator chose a delivery adapter this deploy does not
   * hold. Its own code rather than `bad_request` because the set it was
   * measured against is the deploy's own registry, not the request - the
   * choice was legal when the screen rendered and stopped being legal when the
   * deploy was reconfigured.
   */
  'invalid_provider',
  'internal',
]);
export type ApiErrorCode = z.infer<typeof ApiErrorCode>;

/**
 * Every code a CALLER can end up holding: the server's own vocabulary plus the
 * two the typed client mints for itself when there was no usable answer at all
 * (`client.ts`).
 *
 * Declared here rather than beside `ApiRequestError` because the one renderer
 * that turns a failure into a sentence (`errorSentence` in `@croissants/i18n`)
 * is exhaustive over it, and that package compiles without the DOM lib - it
 * cannot reach `client.ts` at all. This module has no such dependency.
 */
export type ApiFailureCode = ApiErrorCode | 'network_error' | 'invalid_response';

export const ApiError = z.object({
  error: z.object({
    code: ApiErrorCode,
    /**
     * Catalogue key so the client renders the message in the reader's locale.
     *
     * A CLOSED set (`messages.ts`): a key no catalogue carries cannot be put on
     * the wire, because this schema rejects it at both ends. It was `z.string()`
     * once, and the open set is why an operator read "Something went wrong."
     * where the server had sent an actionable provider diagnosis (NFR-006).
     */
    messageKey: ErrorMessageKeySchema,
    /** English developer-facing text. Never shown to a guest verbatim. */
    message: z.string(),
    /** Per-field problems, keyed by dotted path. */
    fields: z.record(z.string(), z.string()).optional(),
    requestId: z.string().optional(),
    /**
     * When the thing that is locked stops being locked - carried by
     * `pin_locked` and by `too_many_requests`, and by nothing else
     * (device-pairing plan 2.2).
     *
     * On the error envelope rather than in `fields` because the counter RENDERS
     * it ("Reessayez dans N minutes") rather than pointing at an input, and it
     * is a server instant the app converts against its own clock offset. A
     * client that does not know the code ignores it, which is what optional is
     * for.
     */
    until: Instant.optional(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;

export const Ok = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof Ok>;

/**
 * The longest note a guest may leave the house (GST-163): the guest app's box,
 * the checkout request, the counter's ticket and Internal's order all stop here.
 */
export const GUEST_NOTE_MAX = 200;
